import type { FieldPolicy, TypePolicies } from "@apollo/client"

// UptimeTarget rows are uniquely identified by their (Source, Identifier)
// pair -- the same composite business key the SQL stored procs use --
// not by an `Id` column. Without this policy Apollo falls back to a
// random cache id per query result, so the mute mutations would update
// the detail-view target but the polling list would keep rendering
// the old, un-muted copy until the next 60s poll. Pinning the key
// fields here makes the normalised cache do that propagation for us.
export const uptimeTypePolicies: TypePolicies = {
  UptimeTarget: {
    keyFields: ["Source", "Identifier"],
  },
}

/**
 * Apollo field policy for `getUptimeTargetsList`.
 *
 * Variant of the generic `paginatedField()` that:
 *   - keys cache entries on the `scope` arg in addition to sort/filters
 *     (so per-platform / per-source / per-bucket fetches don't collide
 *     in the cache),
 *   - merges nested `items` into the existing entry on subsequent
 *     `fetchMore` calls (offset > 0),
 *   - keeps `pageInfo` and `bucketCounts` from the latest response so
 *     the table count strip + page totals stay in sync with the most
 *     recent server snapshot rather than drifting with each
 *     fetchMore call.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const uptimeTargetsListField: FieldPolicy<any> = {
  keyArgs: [
    "scope",
    ["platform", "source", "bucket"],
    "input",
    ["sort", "filters"],
  ],
  merge(existing, incoming, { args }) {
    const offset =
      (args as { input?: { offset?: number } } | null)?.input?.offset ?? 0
    if (offset === 0 || !existing) return incoming
    return {
      ...incoming,
      items: [...existing.items, ...incoming.items],
    }
  },
}

export const uptimeCacheFields = {
  getUptimeTargetsList: uptimeTargetsListField,
}
